import { IQuestion, IQuestionOption, IPriceCondition, ISurgeItem } from '../types';
import {
  DIMENSION_OPTION_TYPE,
  OPERATOR_DIFFERS,
  OPERATOR_EXISTS,
  OPERATOR_GREATER_THAN,
  OPERATOR_LESS_THAN,
  OPERATOR_GREATER_EQUAL,
  OPERATOR_LESS_EQUAL,
  OPERATOR_EQUAL,
  OPERATOR_EQUAL_STRICT,
  OPERATOR_NOT_EQUAL,
  OPERATOR_NOT_EQUAL_STRICT,
  OPERATION_ADD,
  OPERATION_SUBTRACT,
  OPERATION_MULTIPLY,
  RULE_STATE_ACTIVE,
} from '../constants/pricingConstants';
import { OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE } from '../constants';

// ============================================================================
// PUBLIC API - Main Entry Points
// ============================================================================

/**
 * Evaluate value-based pricing rules
 * These rules trigger based on numeric input values (e.g., width > 1300mm)
 *
 * @example
 * // When user enters width = 1500mm
 * // Rule: "Width > 1300mm → Add $35"
 * // Result: Surge of $35
 */
export function evaluateValueBasedRules(
  question: IQuestion,
  userInput: string | number | undefined,
  selectedOption: IQuestionOption | undefined,
  pricingRules: IPriceCondition[],
  allAnswers?: Record<string, IQuestionOption[]>,
  questions?: IQuestion[],
  allMessages?: any[],
  formatAmount?: (amount: number) => string,
  marginPercentage?: number
): ISurgeItem[] {
  if (!pricingRules?.length) return [];

  const activeRules = getActiveValueBasedRules(pricingRules, question.category);
  if (!activeRules.length) return [];

  const surgeItems: ISurgeItem[] = [];

  activeRules.forEach(rule => {
    if (isValueRuleTriggered(rule, question.category, userInput, selectedOption)) {
      const surgeItem = createSurgeItem(rule, {
        optionId: selectedOption?.optionId || '',
        optionName: question.question,
        categoryName: question.categoryName,
      }, undefined, undefined, formatAmount, marginPercentage);
      surgeItems.push(surgeItem);
    }
  });

  return surgeItems;
}

/**
 * Evaluate option-based pricing rules
 * These rules trigger based on combinations of selected options
 *
 * @example
 * // Rule: "When Standard White + (Front OR Back) + 25mm add-ons → $6/meter"
 * // Trigger logic: (Front OR Back) AND Standard White (OR within category, AND across)
 * // Expected: Current selection must match one of the 25mm add-on options
 */
export function evaluateOptionBasedRules(
  currentAnswers: IQuestionOption[],
  allAnswers: Record<string, IQuestionOption[]>,
  pricingRules: IPriceCondition[],
  questions: IQuestion[],
  allMessages?: any[],
  formatAmount?: (amount: number) => string,
  marginPercentage?: number,
  question?: IQuestion,
  isBatchEvaluation?: boolean
): ISurgeItem[] {
  if (!pricingRules?.length) return [];

  const activeRules = getActiveOptionBasedRules(pricingRules);
  if (!activeRules.length) return [];

  const surgeItems: ISurgeItem[] = [];

  activeRules.forEach(rule => {
    // Check 1: Are trigger conditions met? (e.g., Standard White selected)
    if (!areTriggersMatched(rule, allAnswers)) {
      return;
    }

    // Check 2: Does current selection match expected options? (e.g., selecting 25mm add-on)
    if (!isCurrentSelectionExpected(currentAnswers, rule, allAnswers)) {
      return;
    }

    // Check 3 (differs only): the expected option's colour name must differ from the
    // trigger option's colour name. Internal "Agate Grey" and External "Agate Grey"
    // share an ID namespace but the same display name — we compare normalised names
    // rather than IDs so same-colour cross-category combos are correctly suppressed.
    if (rule.dogma_condition_operator === OPERATOR_DIFFERS) {
      const matchedTriggerNames = new Set(
        getTriggerOptions(rule)
          .filter((t: any) => (allAnswers[t.categoryId] || []).some(a => a.optionId === t.optionId))
          .map((t: any) => (t.optionName ?? '').toLowerCase().trim())
      );
      const expectedInCurrent = currentAnswers.filter(a =>
        getExpectedOptions(rule).some((e: any) => e.optionId === a.optionId)
      );
      // Suppress the surge if every matched expected option has the same name as a trigger
      if (expectedInCurrent.every(e =>
        matchedTriggerNames.has((e.optionText ?? '').toLowerCase().trim())
      )) return;
    }

    // Rule triggered! Create surge item
    const triggerOption = findTriggerOption(getTriggerOptions(rule), questions, allAnswers);
    const expectedOption = findExpectedOption(getExpectedOptions(rule), currentAnswers, allAnswers);

    // Extract form field multiplier if multiply operation (e.g., quantity field)
    let formMultiplier = rule.dogma_operation === OPERATION_MULTIPLY
      ? extractFormMultiplier(getExpectedOptions(rule), allMessages, questions)
      : undefined;

    // If dogma_apply_per_unit is true, multiply by frame count respecting config mode.
    // Mirrors displayCondition.ts PER_FRAME_UNIT logic:
    //   individual_configuration → 1 (each frame is asked separately, rule fires per frame)
    //   same_for_all or no config mode → frameCount
    if (rule.dogma_apply_per_unit) {
      const frameMultiplier = extractFrameMultiplier(question, allAnswers, allMessages);
      if (frameMultiplier > 0) {
        formMultiplier = (formMultiplier ?? 1) * frameMultiplier;
      }
    }

    // If dogma_apply_per_sash is true, multiply by sash count respecting config mode.
    // Mirrors displayCondition.ts PER_SASH_UNIT logic:
    //   same_for_all + top/bottom-specific → frameCount (one sash per frame)
    //   same_for_all + both               → numberOfSashes (all sashes across all frames)
    //   individual / single-frame         → 1 (each frame is already asked separately)
    if (rule.dogma_apply_per_sash) {
      const sashMultiplier = extractSashMultiplier(question, rule, allAnswers, allMessages, isBatchEvaluation);
      if (sashMultiplier > 0) {
        formMultiplier = (formMultiplier ?? 1) * sashMultiplier;
      }
    }

    const surgeItem = createSurgeItem(rule, triggerOption, expectedOption, formMultiplier, formatAmount, marginPercentage);

    surgeItems.push(surgeItem);
  });

  return surgeItems;
}

/**
 * Format surge items into a user-friendly message
 */
export function formatSurgeMessage(surgeItems: ISurgeItem[], formatAmount?: (amount: number) => string): string {
  if (!surgeItems?.length) return '';

  const fmt = (amount: number) => formatAmount ? formatAmount(amount) : `€${amount.toFixed(2)}`;

  const totalSurge = surgeItems.reduce((sum, item) => sum + item.surgeAmount, 0);
  const isAddition = totalSurge >= 0;
  const surgeText = isAddition ? 'added' : 'deducted';

  let message = `💰 **Price Surge Alert**\n\n`;
  message += `Based on your selection, the following charges will be ${surgeText}:\n\n`;

  surgeItems.forEach(item => {
    const sign = item.surgeAmount >= 0 ? '+' : '';
    message += `• ${item.ruleName}: ${sign}${fmt(Math.abs(item.surgeAmount))}\n`;
    if (item.description) {
      message += `  _${item.description}_\n`;
    }
  });

  message += `\n**Total ${isAddition ? 'Additional' : 'Discount'}: ${fmt(Math.abs(totalSurge))}**`;
  return message;
}

// ============================================================================
// RULE FILTERING - Get Applicable Rules
// ============================================================================

function getTriggerOptions(rule: IPriceCondition): any[] {
  return rule.dogma_trigger_groups?.flat() ?? rule.dogma_trigger_optionids ?? [];
}

function getExpectedOptions(rule: IPriceCondition): any[] {
  return rule.dogma_expected_groups?.flat() ?? rule.dogma_expected_optionids ?? [];
}

/**
 * Get active value-based rules for a specific category
 */
function getActiveValueBasedRules(
  rules: IPriceCondition[],
  categoryId: string
): IPriceCondition[] {
  return rules.filter(rule => {
    // Must be active
    if (rule.statecode !== RULE_STATE_ACTIVE) return false;

    // Must be value-based (no expected options)
    if (rule.dogma_expected_groups?.length || rule.dogma_expected_optionids?.length) return false;

    // Must have trigger in this category
    const allTriggers = getTriggerOptions(rule);
    return allTriggers.some((trigger: any) => trigger.categoryId === categoryId);
  });
}

/**
 * Get active option-based rules
 */
function getActiveOptionBasedRules(rules: IPriceCondition[]): IPriceCondition[] {
  return rules.filter(rule => {
    // Must be active
    if (rule.statecode !== RULE_STATE_ACTIVE) return false;

    // Must be option-based (has expected options)
    const hasExpected = rule.dogma_expected_groups?.length || rule.dogma_expected_optionids?.length;
    if (!hasExpected) return false;

    // Must use "exists" or "differs" operator
    return rule.dogma_condition_operator === OPERATOR_EXISTS ||
           rule.dogma_condition_operator === OPERATOR_DIFFERS;
  });
}

// ============================================================================
// RULE EVALUATION - Check if Rules Are Triggered
// ============================================================================

/**
 * Check if a value-based rule is triggered
 *
 * @example
 * Rule: "Width > 1300mm"
 * Input: 1500mm
 * Result: true (1500 > 1300)
 */
function isValueRuleTriggered(
  rule: IPriceCondition,
  categoryId: string,
  userInput: string | number | undefined,
  selectedOption: IQuestionOption | undefined
): boolean {
  // Must have matching trigger in this category AND matching the specific option
  const allTriggers = getTriggerOptions(rule);
  const matchingTrigger = allTriggers.find(
    (trigger: any) => trigger.categoryId === categoryId &&
               (selectedOption ? trigger.optionId === selectedOption.optionId : true)
  );

  if (!matchingTrigger) return false;

  const operator = rule.dogma_condition_operator;

  // "exists" operator: just check if value exists
  if (operator === OPERATOR_EXISTS) {
    return userInput !== undefined && userInput !== null && userInput !== '';
  }

  // Numeric operators: compare values
  return evaluateNumericCondition(userInput, operator, rule.dogma_condition_value);
}

/**
 * Evaluate numeric comparison (>, <, >=, <=, ==, !=)
 */
function evaluateNumericCondition(
  userInput: string | number | undefined,
  operator: string | undefined,
  conditionValue: string | undefined
): boolean {
  if (!userInput || !operator) return false;

  const numericValue = parseFloat(String(userInput));
  const threshold = parseFloat(conditionValue || '0');

  if (isNaN(numericValue) || isNaN(threshold)) {
    return false;
  }

  switch (operator) {
    case OPERATOR_GREATER_THAN:
      return numericValue > threshold;
    case OPERATOR_LESS_THAN:
      return numericValue < threshold;
    case OPERATOR_GREATER_EQUAL:
      return numericValue >= threshold;
    case OPERATOR_LESS_EQUAL:
      return numericValue <= threshold;
    case OPERATOR_EQUAL:
    case OPERATOR_EQUAL_STRICT:
      return numericValue === threshold;
    case OPERATOR_NOT_EQUAL:
    case OPERATOR_NOT_EQUAL_STRICT:
      return numericValue !== threshold;
    default:
      return false;
  }
}

/**
 * Check if trigger options are matched
 *
 * Groups path: AND across groups, OR within each group
 * Legacy path: AND across categories, OR within category
 */
function areTriggersMatched(
  rule: IPriceCondition,
  allAnswers: Record<string, IQuestionOption[]>
): boolean {
  if (rule.dogma_trigger_groups?.length) {
    return rule.dogma_trigger_groups.every(group =>
      group.some(t => (allAnswers[t.categoryId] ?? []).some(a => a.optionId === t.optionId))
    );
  }
  const triggers = rule.dogma_trigger_optionids;
  if (!triggers?.length) return false;
  const triggersByCategory = groupByCategory(triggers);
  return Array.from(triggersByCategory.entries()).every(([categoryId, cats]) => {
    const ans = allAnswers[categoryId];
    if (!ans?.length) return false;
    return cats.some(t => ans.some(a => a.optionId === t.optionId));
  });
}

/**
 * Check if current selection matches expected options.
 *
 * Groups path: at least one group must match currentAnswers (the "fire" trigger for
 * this message), and ALL groups must be satisfied when checking currentAnswers first
 * then allAnswers. This allows expected groups that span multiple questions — e.g.
 * Decorative Bar Type AND Top Sash answered in separate messages.
 *
 * Legacy path: flat OR against currentAnswers only.
 */
function isCurrentSelectionExpected(
  currentAnswers: IQuestionOption[],
  rule: IPriceCondition,
  allAnswers: Record<string, IQuestionOption[]>
): boolean {
  if (rule.dogma_expected_groups?.length) {
    // At least one group must be satisfied by the current answer (fire condition).
    const someGroupMatchesCurrent = rule.dogma_expected_groups.some(group =>
      group.some(e => currentAnswers.some(a => a.optionId === e.optionId))
    );
    if (!someGroupMatchesCurrent) return false;

    // All groups must be satisfied — check currentAnswers first, fall back to allAnswers.
    return rule.dogma_expected_groups.every(group =>
      group.some(e => {
        if (currentAnswers.some(a => a.optionId === e.optionId)) return true;
        return (allAnswers[e.categoryId] ?? []).some(a => a.optionId === e.optionId);
      })
    );
  }
  const expected = rule.dogma_expected_optionids;
  if (!expected?.length) return false;
  return expected.some(e => currentAnswers.some(a => a.optionId === e.optionId));
}

// ============================================================================
// DIMENSION CALCULATION - Extract and Sum Dimensions
// ============================================================================

/**
 * Extract form field value to use as multiplier for pricing
 * Used for quantity-based pricing (e.g., number of units)
 *
 * @example
 * Expected option: "Cill quantity" input field
 * User entered: 3
 * Result: 3 (will multiply price by 3)
 */
function extractFrameCount(messages: any[] | undefined): number | undefined {
  if (!messages?.length) return undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.type !== 'user' || !msg.response?.answer?.length) continue;
    for (const opt of msg.response.answer) {
      if (
        (opt.optionType === OPTION_TYPE_VARIATION || opt.optionType === OPTION_TYPE_WINDOW_TYPE) &&
        opt.numberOfFrames > 0
      ) {
        return opt.numberOfFrames;
      }
    }
  }
  return undefined;
}

function extractSashCount(messages: any[] | undefined): number | undefined {
  if (!messages?.length) return undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.type !== 'user' || !msg.response?.answer?.length) continue;
    for (const opt of msg.response.answer) {
      if (
        (opt.optionType === OPTION_TYPE_VARIATION || opt.optionType === OPTION_TYPE_WINDOW_TYPE) &&
        opt.numberOfSashes != null && opt.numberOfSashes > 0
      ) {
        return opt.numberOfSashes;
      }
    }
  }
  return undefined;
}

function extractFrameMultiplier(
  question: IQuestion | undefined,
  allAnswers: Record<string, IQuestionOption[]>,
  messages: any[] | undefined
): number {
  const frameCount = extractFrameCount(messages) ?? extractFrameCountFromAllAnswers(allAnswers) ?? 1;

  if (!question) return frameCount;

  // Per-frame config mode lives in dependsOn categories (same as displayCondition.ts PER_FRAME_UNIT).
  const dependsOn = question.displayCondition?.dependsOn ?? [];
  for (const dep of dependsOn) {
    if (!dep.categoryId) continue;
    const catAnswers = allAnswers[dep.categoryId] ?? [];
    if (catAnswers.some(o => (o as any).responseValue === 'same_for_all_windows')) return frameCount;
    if (catAnswers.some(o => (o as any).responseValue === 'individual_configuration')) return 1;
  }

  // Fallback: check same category (some question flows put config mode in the same category).
  const categoryAnswers = allAnswers[question.category] ?? [];
  if (categoryAnswers.some(o => (o as any).responseValue === 'same_for_all_windows')) return frameCount;
  if (categoryAnswers.some(o => (o as any).responseValue === 'individual_configuration')) return 1;

  // No config mode found → always multiply by frame count.
  return frameCount;
}

const SASH_PLACEMENT_VALUES = new Set(['top', 'bottom', 'both']);

/**
 * Count how many individual frames have all expected groups of the rule satisfied.
 *
 * Builds two maps from messages:
 *   frameAnswers  — answers from questions whose responsePath ends in frameN (per-frame)
 *   sharedAnswers — answers from questions with no frame suffix (asked once for all frames,
 *                   e.g. Decorative Bar Type in individual mode)
 *
 * Each frame's satisfaction check merges its own answers with sharedAnswers so that
 * a shared question (e.g. Int Georgian selection) counts toward every frame.
 */
function countMatchingFramesForRule(rule: IPriceCondition, messages: any[] | undefined): number {
  if (!messages?.length) return 1;

  const frameAnswers = new Map<string, Record<string, any[]>>();
  const sharedAnswers: Record<string, any[]> = {};

  messages.forEach(msg => {
    if (msg.type !== 'user' || !msg.response?.answer?.length) return;
    const responsePath = (msg.question?.responsePath ?? '') as string;
    const catId = msg.question?.category as string;
    if (!catId) return;

    const frameMatch = responsePath.match(/frame(\d+)$/i);
    if (frameMatch) {
      const frameId = frameMatch[1]!;
      if (!frameAnswers.has(frameId)) frameAnswers.set(frameId, {});
      const fa = frameAnswers.get(frameId)!;
      if (!fa[catId]) fa[catId] = [];
      fa[catId].push(...(msg.response.answer as any[]));
    } else {
      // Shared question — no frame suffix, applies to all frames.
      if (!sharedAnswers[catId]) sharedAnswers[catId] = [];
      sharedAnswers[catId].push(...(msg.response.answer as any[]));
    }
  });

  if (frameAnswers.size === 0) return 1;

  let matchingFrameCount = 0;
  const expectedGroups = rule.dogma_expected_groups;

  frameAnswers.forEach(frameAns => {
    // Merge per-frame answers with shared answers for the satisfaction check.
    const merged: Record<string, any[]> = {};
    Object.entries(sharedAnswers).forEach(([cat, opts]) => { merged[cat] = [...opts]; });
    Object.entries(frameAns).forEach(([cat, opts]) => {
      merged[cat] = [...(merged[cat] ?? []), ...opts];
    });

    let satisfied: boolean;
    if (expectedGroups?.length) {
      satisfied = expectedGroups.every(group =>
        group.some(e => (merged[e.categoryId] ?? []).some((a: any) => a.optionId === e.optionId))
      );
    } else {
      const expected = rule.dogma_expected_optionids ?? [];
      satisfied = expected.some(e =>
        (merged[e.categoryId] ?? []).some((a: any) => a.optionId === e.optionId)
      );
    }
    if (satisfied) matchingFrameCount++;
  });

  return matchingFrameCount > 0 ? matchingFrameCount : 1;
}

function extractFrameCountFromAllAnswers(allAnswers: Record<string, IQuestionOption[]>): number | undefined {
  for (const opts of Object.values(allAnswers)) {
    for (const opt of opts) {
      const nf = (opt as any).numberOfFrames;
      if (nf && nf > 0) return nf;
    }
  }
  return undefined;
}

function extractSashMultiplier(
  question: IQuestion | undefined,
  rule: IPriceCondition,
  allAnswers: Record<string, IQuestionOption[]>,
  messages: any[] | undefined,
  isBatchEvaluation?: boolean
): number {
  const numberOfSashes = extractSashCount(messages) ?? 1;
  const frameCount = extractFrameCount(messages) ?? extractFrameCountFromAllAnswers(allAnswers) ?? 1;

  if (!question) return numberOfSashes;

  const categoryAnswers = allAnswers[question.category] ?? [];
  let isSameForAll = categoryAnswers.some(o => (o as any).responseValue === 'same_for_all_windows');
  let isIndividual = categoryAnswers.some(o => (o as any).responseValue === 'individual_configuration');

  // Config mode may live in a dependsOn category rather than the question's own category.
  // Mirror the same check used in extractFrameMultiplier.
  if (!isSameForAll && !isIndividual) {
    const dependsOn = question.displayCondition?.dependsOn ?? [];
    for (const dep of dependsOn) {
      if (!dep.categoryId) continue;
      const depAnswers = allAnswers[dep.categoryId] ?? [];
      if (depAnswers.some(o => (o as any).responseValue === 'same_for_all_windows')) {
        isSameForAll = true; break;
      }
      if (depAnswers.some(o => (o as any).responseValue === 'individual_configuration')) {
        isIndividual = true; break;
      }
    }
  }

  const placement = categoryAnswers
    .map(o => (o as any).responseValue as string)
    .find(v => SASH_PLACEMENT_VALUES.has(v));

  // Top Sash = optionType 24, Bottom Sash = optionType 23 (from EcoSlide constants).
  // Using optionType (number) is more reliable than matching optionTypeName strings.
  const TOP_SASH_OPTION_TYPE = 24;
  const BOTTOM_SASH_OPTION_TYPE = 23;
  const isTopOrBottomSash =
    question.optionType === TOP_SASH_OPTION_TYPE ||
    question.optionType === BOTTOM_SASH_OPTION_TYPE;

  // Also detect top/bottom sash specificity from the rule's expected groups — the rule
  // may fire from a Decorative Bar question but have Top/Bottom Sash options in expected groups.
  const allExpected = getExpectedOptions(rule);
  const isTopOrBottomSashFromRule = allExpected.some(
    e => e.optionType === TOP_SASH_OPTION_TYPE || e.optionType === BOTTOM_SASH_OPTION_TYPE
  );
  const isTopOrBottomSashEffective = isTopOrBottomSash || isTopOrBottomSashFromRule;

  if (isSameForAll) {
    if (placement === 'both') return numberOfSashes;
    if (placement === 'top' || placement === 'bottom') return frameCount;
    if (isTopOrBottomSashEffective) return frameCount;
    return numberOfSashes;
  }

  if (isIndividual) {
    // Batch: count all matching frames → total correct price (£18 × 2 frames = £36).
    // Real-time: return 1 so each frame adds its own £18 surge message independently;
    // the frame-aware removal logic in AssistantContext keeps both visible.
    if (isBatchEvaluation) return countMatchingFramesForRule(rule, messages);
    return 1;
  }

  // Single-frame product (no config mode question in flow).
  if (isTopOrBottomSashEffective) return 1;
  if (placement === 'both') return numberOfSashes;
  if (placement === 'top' || placement === 'bottom') return 1;
  return numberOfSashes;
}

function extractFormMultiplier(
  expectedOptions: any[] | undefined,
  messages: any[] | undefined,
  questions: IQuestion[]
): number | undefined {
  if (!expectedOptions?.length || !messages?.length) return undefined;

  // Look for form input fields in expected options
  for (const expectedOpt of expectedOptions) {
    // Find the question for this option
    const question = questions.find(q => q.category === expectedOpt.categoryId);
    if (!question) continue;

    // Find the option details
    const option = question.options?.find(opt => opt.optionId === expectedOpt.optionId);
    if (!option) continue;

    // Skip dimension fields (they're handled separately)
    if (option.optionType === DIMENSION_OPTION_TYPE) continue;

    // Look for form data in messages
    for (const msg of messages) {
      if (msg.type !== 'user' || !msg.response?.form || msg.question?.category !== question.category) continue;

      const formData = msg.response.form;
      const formValue = formData[option.responseValue];

      if (formValue !== undefined && formValue !== null && formValue !== '') {
        const numValue = parseFloat(String(formValue));
        if (!isNaN(numValue) && numValue > 0) {
          return numValue;
        }
      }
    }
  }

  return undefined;
}

// ============================================================================
// SURGE AMOUNT CALCULATION
// ============================================================================

/**
 * Calculate the surge amount for a pricing rule
 *
 * @example
 * // Form field multiplier (quantity)
 * Rule: $10 per unit, Operation: multiply
 * Quantity: 3
 * Result: $10 × 3 = $30
 */
function calculateSurgeAmount(
  rule: IPriceCondition,
  formMultiplier?: number
): number {
  const { dogma_operation, dogma_price_value } = rule;

  // Validate price value
  if (dogma_price_value === undefined || dogma_price_value === null || isNaN(dogma_price_value)) {
    return 0;
  }

  let amount = 0;

  switch (dogma_operation?.toLowerCase()) {
    case OPERATION_ADD:
      amount = dogma_price_value;
      break;
    case OPERATION_SUBTRACT:
      amount = -Math.abs(dogma_price_value);
      break;
    case OPERATION_MULTIPLY:
      amount = dogma_price_value;
      break;
    default:
      return 0;
  }

  // Apply form field multiplier (for quantity-based pricing)
  if (formMultiplier !== undefined && formMultiplier !== null) {
    if (formMultiplier <= 0) {
      return 0;
    }
    amount *= formMultiplier;
  }

  return amount;
}

// ============================================================================
// SURGE ITEM CREATION
// ============================================================================

/**
 * Create a surge item from a triggered rule
 */
/**
 * Applies margin (as a % of the final selling price, not a markup on cost)
 * to a signed amount: sellingAmount = costAmount / (1 - marginPercentage/100).
 * Preserves sign since a surge amount can itself be negative (a "subtract"
 * rule). Bails out (returns the cost amount unchanged) for a 100%+ margin,
 * where the formula is undefined.
 */
function applyMarginDivide(costAmount: number, marginPercentage: number): number {
  const sign = costAmount < 0 ? -1 : 1;
  const divisor = 1 - marginPercentage / 100;
  if (divisor <= 0) return costAmount;
  return sign * Math.round((Math.abs(costAmount) / divisor) * 100) / 100;
}

function createSurgeItem(
  rule: IPriceCondition,
  triggerOption: any,
  expectedOption?: any,
  formMultiplier?: number,
  formatAmount?: (amount: number) => string,
  marginPercentage?: number
): ISurgeItem {
  const vssSurgeAmount = calculateSurgeAmount(rule, formMultiplier);

  // discountPercentage/discountAmount are merged into the rule at config-transform time
  // from pricingRuleDiscounts — read directly, no external map needed.
  // vssSurgeAmount stays raw (VSS/cost-price track never discounted) — the discount
  // is tracked separately as vssDiscountAmount, purely informational for VSS reporting.
  // The customer-facing surgeAmount DOES bake the discount in (then margin on top) —
  // these are two independent price tracks.
  const sign = vssSurgeAmount < 0 ? -1 : 1;
  const vssDiscountAmount = (rule.discountAmount ?? 0) > 0
    ? Math.round(rule.discountAmount! * 100) / 100
    : (rule.discountPercentage ?? 0) > 0
      ? Math.round(Math.abs(vssSurgeAmount) * (rule.discountPercentage! / 100) * 100) / 100
      : undefined;

  const discountedForCustomer = vssDiscountAmount
    ? sign * Math.max(0, Math.round((Math.abs(vssSurgeAmount) - vssDiscountAmount) * 100) / 100)
    : vssSurgeAmount;

  const surgeAmount = (marginPercentage && discountedForCustomer !== 0)
    ? applyMarginDivide(discountedForCustomer, marginPercentage)
    : discountedForCustomer;

  const description = buildDescription(rule, triggerOption, expectedOption, formMultiplier, formatAmount);

  return {
    ruleId: rule.dogma_pricingruleid,
    ruleName: rule.dogma_name,
    description,
    surgeAmount,
    vssSurgeAmount,
    vssDiscountAmount,
    operation: rule.dogma_operation,
    applyPerUnit: rule.dogma_apply_per_unit,
    applyPerSash: rule.dogma_apply_per_sash,
    resolverValue: rule.resolverValue,
    triggerOption,
  };
}

/**
 * Build a user-friendly description for the surge
 */
function buildDescription(
  rule: IPriceCondition,
  triggerOption: any,
  expectedOption?: any,
  formMultiplier?: number,
  _formatAmount?: (amount: number) => string
): string {
  let description = '';

  if (expectedOption) {
    // Option-based rule: "When combined with Standard White"
    description = `When combined with ${triggerOption?.optionName || 'selection'}`;
  } else {
    // Value-based rule: "×€6 (when selected)" or "+€35 (when > 1300)"
    description = formatRuleCondition(rule);
  }

  // Add form multiplier info (e.g., quantity)
  if (formMultiplier !== undefined && formMultiplier !== null && formMultiplier > 0) {
    description += ` (qty: ${formMultiplier})`;
  }

  return description;
}

/**
 * Format the rule condition for display
 */
function formatRuleCondition(rule: IPriceCondition): string {
  const { dogma_condition_operator, dogma_condition_value } = rule;

  // Get condition text
  let condition = '';
  if (dogma_condition_operator === OPERATOR_EXISTS) {
    condition = 'selected';
  } else if (dogma_condition_value !== undefined && dogma_condition_value !== null) {
    condition = `${dogma_condition_operator} ${dogma_condition_value}`;
  } else {
    condition = dogma_condition_operator || '';
  }

  // Deliberately does NOT include the raw dogma_price_value here — that's the
  // rule's pre-margin/pre-discount config value, which doesn't match the
  // actual (margin/discount-adjusted) amount shown as the surge's main price,
  // and changes independently of it whenever margin changes. Only the
  // condition itself is stable/meaningful to show as a description.
  return condition ? `(when ${condition})` : '';
}

// ============================================================================
// UTILITY HELPERS
// ============================================================================

/**
 * Group items by categoryId
 */
function groupByCategory(items: any[]): Map<string, any[]> {
  const grouped = new Map<string, any[]>();

  items.forEach(item => {
    const categoryId = item.categoryId;
    if (!grouped.has(categoryId)) {
      grouped.set(categoryId, []);
    }
    grouped.get(categoryId)!.push(item);
  });

  return grouped;
}

/**
 * Find the trigger option that's actually selected in allAnswers
 * IMPORTANT: Returns the trigger that's actually in user's selections, not just the first one
 */
function findTriggerOption(
  triggerOptions: any[] | undefined,
  questions: IQuestion[],
  allAnswers?: Record<string, IQuestionOption[]>
): any {
  if (!triggerOptions?.length) return undefined;

  // If allAnswers provided, find the trigger that's actually selected
  if (allAnswers) {

    for (const trigger of triggerOptions) {
      const categoryAnswers = allAnswers[trigger.categoryId];
      if (categoryAnswers?.some((answer: any) => answer.optionId === trigger.optionId)) {
        const question = questions.find(q => q.category === trigger.categoryId);
        return {
          optionId: trigger.optionId,
          optionName: trigger.optionName,
          categoryName: question?.categoryName || trigger.categoryId,
        };
      }
    }
  }

  // Fallback: return first trigger if no match found
  const trigger = triggerOptions[0];
  const question = questions.find(q => q.category === trigger.categoryId);

  return {
    optionId: trigger.optionId,
    optionName: trigger.optionName,
    categoryName: question?.categoryName || trigger.categoryId,
  };
}

/**
 * Find which expected option was selected (from current or previous answers)
 */
function findExpectedOption(
  expectedOptions: any[] | undefined,
  currentAnswers: IQuestionOption[],
  allAnswers: Record<string, IQuestionOption[]>
): any {
  if (!expectedOptions?.length) return undefined;

  // Check current answers first
  let match = expectedOptions.find(opt =>
    currentAnswers.some(answer => answer.optionId === opt.optionId)
  );

  // Fall back to previous answers
  if (!match) {
    match = expectedOptions.find(opt => {
      const categoryAnswers = allAnswers[opt.categoryId];
      return categoryAnswers?.some(answer => answer.optionId === opt.optionId);
    });
  }

  return match;
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

export function getQuestionPricingRules(
  question: IQuestion,
  allPricingRules: IPriceCondition[]
): IPriceCondition[] {
  if (!question || !allPricingRules?.length) return [];

  return allPricingRules.filter(rule => {
    if (rule.statecode !== RULE_STATE_ACTIVE) return false;
    const allTriggers = getTriggerOptions(rule);
    return allTriggers.some((trigger: any) => trigger.categoryId === question.category);
  });
}

export function hasValueBasedRules(
  question: IQuestion,
  allPricingRules: IPriceCondition[]
): boolean {
  const rules = getQuestionPricingRules(question, allPricingRules);
  return rules.some(rule =>
    !rule.dogma_expected_groups?.length && !rule.dogma_expected_optionids?.length
  );
}

/**
 * Evaluate all surge prices from a collection of messages
 * This function iterates through all user messages and evaluates pricing rules
 * to calculate surge prices based on the complete configuration
 *
 * @param messages - Array of assistant messages (bot and user responses)
 * @param questionFlow - Complete question flow for reference
 * @returns Array of surge messages (one per question that triggers surges)
 */
export function evaluateAllSurgePricesFromMessages(
  messages: any[],
  questionFlow: any[],
  formatAmount?: (amount: number) => string,
  marginPercentage?: number
): any[] {
  const surgeMessages: any[] = [];

  // Get all answers from messages for cross-question rule evaluation
  // Use a Map to track latest answer per question (prevents duplicates from edited questions)
  const answersByQuestion = new Map<string, { categoryId: string; answers: any[] }>();

  messages.forEach(msg => {
    if (msg.type === 'user' && msg.question?.category && msg.question?.questionId && msg.response?.answer) {
      const questionId = msg.question.questionId;
      const categoryId = msg.question.category;

      // Store/overwrite with latest answer for this question
      answersByQuestion.set(questionId, {
        categoryId,
        answers: msg.response.answer
      });
    } else if (msg.type === 'user') {
    }
  });

  // Build allAnswers from the latest answer for each question
  const allAnswers: Record<string, any[]> = {};
  answersByQuestion.forEach(({ categoryId, answers }) => {
    if (!allAnswers[categoryId]) {
      allAnswers[categoryId] = [];
    }
    allAnswers[categoryId].push(...answers);
  });

  // Build a discount map from the live questionFlow so that rules from OLD stored
  // messages (which lack discountPercentage) get the correct merged discount.
  // questionFlow already has discountPercentage merged via applyPricingRuleDiscounts.
  const liveRuleDiscountMap = new Map<string, { discountPercentage?: number; discountAmount?: number }>();
  (questionFlow ?? []).forEach((q: any) => {
    const collect = (pc: any) => {
      if ((pc.discountPercentage ?? 0) > 0 || (pc.discountAmount ?? 0) > 0) {
        liveRuleDiscountMap.set(pc.dogma_pricingruleid, {
          discountPercentage: pc.discountPercentage,
          discountAmount: pc.discountAmount,
        });
      }
    };
    (q.priceConditions ?? []).forEach(collect);
    (q.options ?? []).forEach((o: any) => (o.priceConditions ?? []).forEach(collect));
  });

  const enrichRule = (rule: any): any => {
    if ((rule.discountPercentage ?? 0) > 0 || (rule.discountAmount ?? 0) > 0) return rule;
    const d = liveRuleDiscountMap.get(rule.dogma_pricingruleid);
    return d ? { ...rule, ...d } : rule;
  };

  // Tracks option-based rules that have already fired (prevents double-charge when
  // expected groups span multiple questions and the rule fires from several messages).
  const firedOptionBasedRuleIds = new Set<string>();

  // Iterate through each user message to evaluate pricing rules
  messages.forEach(msg => {
    const questionSurgeItems: ISurgeItem[] = [];
    if (msg.type !== 'user' || !msg.question) return;

    const question = msg.question;
    const answer = msg.response.answer || [];
    const pricingRules: any[] = [];

    // Collect pricing rules from question level
    if (question.priceConditions?.length) {
      pricingRules.push(...question.priceConditions);
    }

    // Collect pricing rules from option level
    answer.forEach((option: any) => {
      if (option.priceConditions?.length) {
        pricingRules.push(...option.priceConditions);
      }
    });

    if (pricingRules.length === 0) return;

    // Enrich stored rules with fresh discount info from the live questionFlow.
    // Old stored messages have priceConditions without discountPercentage/discountAmount.
    const enrichedRules = pricingRules.map(enrichRule);


    // Check if this is a form response
    const hasFormData = msg.response.form && Object.keys(msg.response.form).length > 0;

    // Evaluate value-based rules for answer options (skip if form data present)
    if (Array.isArray(answer) && answer.length > 0 && !hasFormData) {
      answer.forEach((option: any) => {
        const surgeItems = evaluateValueBasedRules(
          question,
          option.responseValue || option.optionText,
          option,
          enrichedRules,
          allAnswers,
          questionFlow,
          messages,
          formatAmount,
          marginPercentage
        );
        questionSurgeItems.push(...surgeItems);
      });
    }

    // Evaluate value-based rules for form responses
    if (hasFormData) {
      const formData = msg.response.form;

      // Coupled/Bay: individual frame widths present → always skip overall_width.
      // For coupled/bay windows the width surcharge is checked per frame only;
      // for standard windows only overall_width is present so it is always evaluated.
      const hasFrameWidths = Object.keys(formData).some(k => /^window_\d+_width_/i.test(k));

      Object.entries(formData).forEach(([fieldKey, fieldValue]) => {
        if (fieldKey.startsWith('__')) return;
        if (hasFrameWidths && /^(overall_width|width)_\(mm\)$/.test(fieldKey)) return;

        // Find the option from live question.options, falling back to questionFlow
        // (question.options is slimmed from storage, so the fallback is essential).
        const matchingOption = question.options?.find(
          (opt: any) => opt.responseValue === fieldKey || opt.optionId === fieldKey
        ) ?? questionFlow
          ?.find((q: any) => q.questionId === question.questionId)
          ?.options?.find((opt: any) => opt.responseValue === fieldKey || opt.optionId === fieldKey);

        if (matchingOption || fieldKey === 'textValue') {
          const surgeItems = evaluateValueBasedRules(
            question,
            fieldValue as string | number,
            matchingOption,
            enrichedRules,
            allAnswers,
            questionFlow,
            messages,
            formatAmount,
            marginPercentage
          );
          questionSurgeItems.push(...surgeItems);
        }
      });
    }

    // Evaluate option-based rules
    const optionBasedSurgeItems = evaluateOptionBasedRules(
      answer,
      allAnswers,
      enrichedRules,
      questionFlow,
      messages,
      formatAmount,
      marginPercentage,
      question,
      true  // isBatchEvaluation — uses countMatchingFramesForRule for per-sash individual mode
    );
    // Per-sash rules fire once total — extractSashMultiplier already counts matching
    // frames internally, so dedup by ruleId alone prevents double-charging from
    // multiple messages (e.g. Decorative Bar + Top Sash in the same frame).
    //
    // Per-unit rules fire once per frame in individual mode — dedup by ruleId+frameId
    // allows frame1 and frame2 to each contribute their own charge while preventing
    // the same frame from firing the rule from two different expected-group messages.
    const frameId = (question?.responsePath ?? '').match(/frame(\d+)$/i)?.[1] ?? 'default';
    const newOptSurges = optionBasedSurgeItems.filter(item => {
      const dedupKey = item.applyPerSash ? item.ruleId : `${item.ruleId}_frame${frameId}`;
      return !firedOptionBasedRuleIds.has(dedupKey);
    });
    newOptSurges.forEach(item => {
      const dedupKey = item.applyPerSash ? item.ruleId : `${item.ruleId}_frame${frameId}`;
      firedOptionBasedRuleIds.add(dedupKey);
    });
    questionSurgeItems.push(...newOptSurges);

    // Remove duplicates — same rule on the same option is a duplicate,
    // but the same rule triggered by different frame options is not.
    const uniqueQuestionSurgeItems = questionSurgeItems.filter((item, index, self) =>
      index === self.findIndex(t =>
        t.ruleId === item.ruleId &&
        (t.triggerOption?.optionId || '') === (item.triggerOption?.optionId || '')
      )
    );

    // If this question triggered surge items, create a surge message
    if (uniqueQuestionSurgeItems.length > 0) {
      const surgeMessage = {
        id: `surge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'price-surge',
        message: formatSurgeMessage(uniqueQuestionSurgeItems, formatAmount),
        question: question,
        response: msg.response,
        surgeItems: uniqueQuestionSurgeItems
      };
      surgeMessages.push(surgeMessage);
    }
  });

  return surgeMessages;
}
